/**
 * ModelInfoTooltip — Hover/focus tooltip showing model details.
 *
 * Displays quality score, cost, privacy level, compat test results,
 * and vetting status for a RoleFilteredModel.
 *
 * @module @humanizer/studio/components/settings/ModelInfoTooltip
 */

import { useState, useRef, useEffect, type ReactNode } from 'react';
import type { RoleFilteredModel } from '../../types/electron';

interface ModelInfoTooltipProps {
  model: RoleFilteredModel;
  children?: ReactNode;
}

function formatCostPerMillion(costPer1k: { input: number; output: number }): string {
  if (costPer1k.input === 0 && costPer1k.output === 0) return 'Free';
  const fmtNum = (n: number) => {
    const perM = n * 1000;
    return perM >= 1 ? `$${perM.toFixed(perM % 1 === 0 ? 0 : 1)}` : `$${perM.toFixed(2)}`;
  };
  return `${fmtNum(costPer1k.input)}/1M in \u00B7 ${fmtNum(costPer1k.output)}/1M out`;
}

function privacyText(level: string): string {
  switch (level) {
    case 'local': return 'Local (on-device)';
    case 'cloud-trusted': return 'Cloud (Humanizer)';
    default: return 'Third-Party';
  }
}

function vettingText(status?: string): string {
  switch (status) {
    case 'approved': return 'Approved';
    case 'warning': return 'Warning';
    case 'failed': return 'Failed';
    default: return 'Unvetted';
  }
}

export function ModelInfoTooltip({ model, children }: ModelInfoTooltipProps) {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLSpanElement>(null);

  const show = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setVisible(true);
  };
  const hide = () => {
    timerRef.current = setTimeout(() => setVisible(false), 150);
  };

  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  const qualityPct = Math.round(model.qualityScore * 100);
  const cost = formatCostPerMillion(model.costPer1kTokens);

  return (
    <span
      ref={containerRef}
      className="model-tooltip__trigger"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      tabIndex={0}
      role="button"
      aria-label={`Info about ${model.name}`}
    >
      {children ?? <span className="model-tooltip__icon" aria-hidden="true">&#x2139;</span>}
      {visible && (
        <div className="model-tooltip__popup" role="tooltip">
          <div className="model-tooltip__header">{model.name}</div>
          {model.description && model.description !== model.name && (
            <div className="model-tooltip__desc">{model.description}</div>
          )}
          <div className="model-tooltip__rows">
            <div className="model-tooltip__row">
              <span className="model-tooltip__label">Quality</span>
              <span className="model-tooltip__value">
                <span className="model-tooltip__bar">
                  <span className="model-tooltip__bar-fill" style={{ width: `${qualityPct}%` }} />
                </span>
                {qualityPct}%
              </span>
            </div>
            <div className="model-tooltip__row">
              <span className="model-tooltip__label">Cost</span>
              <span className="model-tooltip__value">{cost}</span>
            </div>
            <div className="model-tooltip__row">
              <span className="model-tooltip__label">Privacy</span>
              <span className="model-tooltip__value">{privacyText(model.privacyLevel)}</span>
            </div>
            {model.compatSummary && (
              <div className="model-tooltip__row">
                <span className="model-tooltip__label">Compat</span>
                <span className="model-tooltip__value">
                  Passed {model.compatSummary.passed}/{model.compatSummary.total} tests
                </span>
              </div>
            )}
            <div className="model-tooltip__row">
              <span className="model-tooltip__label">Status</span>
              <span className="model-tooltip__value">{vettingText(model.vettingStatus)}</span>
            </div>
          </div>
        </div>
      )}
    </span>
  );
}
